import type { AxiosProgressEvent, AxiosRequestConfig } from 'axios'
import axios from 'axios'
import { toast } from 'vue-sonner'
import router from '@/router'
import { usePageStore } from '@/stores/page'

export const useApi = () => {
  const page = usePageStore()

  let controller = new AbortController()
  const config: AxiosRequestConfig = {
    baseURL: import.meta.env.VITE_BASE_API,
    timeout: 300000, // 300s / 5m
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json',
      Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    signal: controller.signal,
    withCredentials: true,
    withXSRFToken: true,
  }

  const REQUEST = async <T>(conf: AxiosRequestConfig, retryIfCsrfError = true): Promise<T> => {
    try {
      const response = await axios.request({ ...config, ...conf })
      return response.data
    } catch (error) {
      if (isCsrfTokenError(error) && retryIfCsrfError) {
        await GET('/sanctum/csrf-cookie')
        return await REQUEST<T>(conf, false)
      } else {
        handleErrors(error)
        return Promise.reject(error)
      }
    }
  }
  const GET = <T>(url: string, params = {}) => {
    return REQUEST<T>({ method: 'get', url, params })
  }

  const POST = <T>(url: string, data: any = {}) => {
    // If sending FormData, ensure multipart header is applied
    if (data instanceof FormData) {
      // Don't set Content-Type header explicitly for FormData;
      // letting browser set it (with boundary) avoids empty payload issues.
      const headers = { ...config.headers }
      delete (headers as Record<string, unknown>)['Content-Type']
      return REQUEST<T>({
        headers,
        method: 'post',
        url,
        data,
      })
    }
    return REQUEST<T>({ method: 'post', url, data })
  }

  const PUT = <T>(url: string, data?: any) => {
    if (data instanceof FormData) {
      const headers = { ...config.headers }
      delete (headers as Record<string, unknown>)['Content-Type']
      return REQUEST<T>({
        headers,
        method: 'put',
        url,
        data,
      })
    }
    return REQUEST<T>({ method: 'put', url, data })
  }
  const DELETE = <T>(url: string, data = {}) => {
    return REQUEST<T>({ method: 'delete', url, data })
  }
  const POSTFORMDATA = <T>(
    url: string,
    formData: FormData,
    onUploadProgress?: (event: AxiosProgressEvent) => void,
  ) => {
    const headers = { ...config.headers }
    delete (headers as Record<string, unknown>)['Content-Type']
    return REQUEST<T>({
      headers,
      method: 'post',
      url,
      data: formData,
      onUploadProgress,
    })
  }

  const PUTFORMDATA = <T>(
    url: string,
    formData: FormData,
    onUploadProgress?: (event: AxiosProgressEvent) => void,
  ) => {
    const headers = { ...config.headers }
    delete (headers as Record<string, unknown>)['Content-Type']
    return REQUEST<T>({
      headers,
      method: 'put',
      url,
      data: formData,
      onUploadProgress,
    })
  }

  const downloadFile = (response: BlobPart, filename: string) => {
    const href = window.URL.createObjectURL(new Blob([response]))
    const link = document.createElement('a')
    link.href = href
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()

    // Cleanup
    window.URL.revokeObjectURL(href)
    document.body.removeChild(link)
  }

  const DOWNLOAD = async (url: string, filename: string, params?: object) => {
    const response = await REQUEST<BlobPart>({
      url,
      method: 'get',
      responseType: 'arraybuffer',
      headers: undefined,
      params: params,
    })

    downloadFile(response, filename)
  }

  const downloadFileFromResponse = (response: any, fallbackFilename: string = 'download.xlsx') => {
    const contentDisposition = response.headers?.['content-disposition']
    let filename = fallbackFilename

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    const href = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = href
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()

    // Cleanup
    window.URL.revokeObjectURL(href)
    document.body.removeChild(link)
  }

  const DOWNLOADDATA = async (url: string, data: object = {}) => {
    const response = await axios.request({
      ...config,
      url,
      method: 'get',
      responseType: 'arraybuffer',
      headers: {
        ...config.headers,
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      params: data,
    })

    downloadFileFromResponse(response)
  }

  const onError = {
    unauthorized: () => {
      const currentRoute = router.currentRoute.value
      const shouldRedirectToHome = () => {
        return ['login', 'forget password', 'unauthorized', 'error'].includes(currentRoute?.name as string)
      }
      if (currentRoute && !shouldRedirectToHome()) {
        const path = currentRoute.fullPath.substring(1)
        const query = path ? { r: path } : undefined
        router.replace({ name: 'login', query })
      }
    },
    maintenance: () => {
      page.setMaintenance()
    },
    forbidden: () => {
      page.showForbiddenError()
      const currentRoute = router.currentRoute.value
      const isAlreadyInError = ['unauthorized', 'error', 'not found', 'login'].includes(currentRoute?.name as string)
      if (currentRoute && !isAlreadyInError) {
        router.replace({ name: 'unauthorized' })
      }
    },
    notFound: () => {
      page.showNotFoundError()
    },
    validationFailed: (errors: FormError) => {
      toast.error('Terdapat kesalahan pada data yang dikirim')
      setTimeout(() => {
        const errorMessage = document.querySelector('.error-message')
        errorMessage?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
      throw new ValidationErrorException(errors)
    },
    tooManyRequest: () => {
      toast.error('Terlalu banyak melakukan request, tunggu beberapa menit sebelum mencoba kembali')
    },
    internalServerError: () => {
      router.replace({ name: 'error' })
    },
    networkError: () => {
      toast.error('Tidak terhubung dengan internet')
    },
  }

  /* https://github.com/axios/axios#handling-errors */
  const handleErrors = (error: unknown) => {
    if (!axios.isAxiosError(error)) {
      /**
       * Not axios error
       * Something happened in setting up the request that triggered an Error
       */
      console.error('[NotAxiosError]', error)
      return
    }

    if (error.response) {
      /**
       * The request was made and the server responded with a
       * status code that falls out of the range of 2xx
       */
      switch (error.response.status) {
        case 401:
          onError.unauthorized()
          break
        case 403:
          onError.forbidden()
          break
        case 404:
          onError.notFound()
          break
        case 422:
          onError.validationFailed(error.response.data.errors)
          break
        case 429:
          onError.tooManyRequest()
          break
        case 500:
          onError.internalServerError()
          break
        case 503:
          onError.maintenance()
          break
        default:
          break
      }
      if (error.message === 'Network Error') {
        onError.networkError()
      }
      console.error('[Axios error]', error)
      return 'AxiosError'
    }

    /**
     * The request was made but no response was received.
     * `error.request` is an instance of XMLHttpRequest
     * in the browser and an instance of
     * http.ClientRequest in node.js
     */
    // if (error.request) {
    // }
    console.error('[API] Axios error but no response', error)
    return
  }

  const formErrors = (error: unknown): FormError => {
    if (error instanceof ValidationErrorException) {
      return error.getErrors()
    }
    return {}
  }

  const isCsrfTokenError = (error: unknown): boolean => {
    return axios.isAxiosError(error) && error.response?.status === 419
  }

  return {
    GET,
    POST,
    DELETE,
    PUT,
    POSTFORMDATA,
    PUTFORMDATA,
    DOWNLOAD,
    DOWNLOADDATA,
    config,
    formErrors,
    abort: () => {
      controller.abort()
      controller = new AbortController()
      config.signal = controller.signal
    },
  }
}

export class UnauthenticatedException extends Error {
  constructor () {
    super()
    this.name = 'UnauthenticatedException'
  }
}

export class ValidationErrorException extends Error {
  constructor (private errors: FormError) {
    super()
    this.name = 'ValidationErrorException'
  }
  getErrors () {
    return this.errors
  }
}

export class NotFoundException extends Error {
  constructor () {
    super()
    this.name = 'NotFoundException'
  }
}

// export class ForbiddenException extends Error {
//   constructor() {
//     super()
//     this.name = 'ForbiddenException'
//   }
// }
